import type { ZodType, ZodTypeDef } from "zod"

/**
 * Evaluates to true if we are in a browser or sufficiently mocked DOM environment.
 * Note: Took this from the React codebase where it's undocumented/private.
 */
export const canUseDOM = Boolean(
  typeof window !== "undefined" &&
    typeof window.document !== "undefined" &&
    typeof window.document.createElement !== "undefined"
)

export function findFocusableAncestor(
  element: HTMLElement
): HTMLElement | undefined {
  let ancestor: HTMLElement | undefined = element
  while (ancestor && ancestor.tabIndex < 0) {
    ancestor = ancestor.parentElement ?? undefined
  }
  return ancestor
}

export function getScrollableAncestor(element: Element): Element {
  let ancestor: Element | undefined = element

  while (ancestor && !isScrollable(ancestor)) {
    ancestor = ancestor.parentElement ?? undefined
  }

  // Fix for: Chrome's body returns an overflowY of "auto" even when it's html that scrolls.
  if (ancestor === document.body)
    ancestor = document.scrollingElement ?? undefined

  return ancestor ?? document.scrollingElement ?? document.documentElement
}

function isScrollable(element: Element): boolean {
  const style = getComputedStyle(element)
  return /auto|scroll/.test(style.overflow + style.overflowX + style.overflowY)
}

// Note that for this to work as intended with a scroller ancestor,
// that ancestor must not have position: "static" (usually "relative" will do nicely).
export function getOffsetTopRelativeTo(node: HTMLElement, ancestor: Element) {
  let result = 0
  let currentNode: HTMLElement | null = node

  while (currentNode && currentNode !== ancestor) {
    result += currentNode.offsetTop

    const parent: Element | null = currentNode.offsetParent
    currentNode = parent instanceof HTMLElement ? parent : null
  }

  return result
}

export function isScrolledToBottom(scroller: Element, threshold = 50) {
  const distanceToBottom =
    scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight

  return distanceToBottom < threshold
}

/**
 * Returns whether the element is partially visible within the currently scrolled viewport.
 */
export function isElementInView(element: Element) {
  const rect = element.getBoundingClientRect()

  const inViewVertically =
    rect.top + rect.height >= 0 && rect.top <= window.innerHeight

  const inViewHorizontally =
    rect.left + rect.width >= 0 && rect.left <= window.innerWidth

  return inViewVertically && inViewHorizontally
}

export const localStorage = {
  isAvailable: () => {
    const test = "__$$__test"

    try {
      window.localStorage.setItem(test, test)
      window.localStorage.removeItem(test)
      return true
    } catch (e) {
      return false
    }
  },

  read: (key: string): string | undefined => {
    try {
      return window.localStorage.getItem(key) ?? undefined
    } catch {
      return undefined
    }
  },

  write: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // Ignored. Best effort.
    }
  },

  readJSON: <Value>(
    key: string,
    validator: ZodType<Value, ZodTypeDef, unknown>
  ): Value | undefined => {
    try {
      const json = localStorage.read(key)
      if (!json) return undefined

      const parsed: unknown = JSON.parse(json)
      const validated = validator.safeParse(parsed)

      if (!validated.success) return undefined

      return validated.data
    } catch {
      return undefined
    }
  }
}
